import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface FinalizeSessionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  waitlistCount?: number;
}

function FinalizeSessionModal({
  isOpen,
  onClose,
  onConfirm,
  waitlistCount = 0,
}: FinalizeSessionModalProps) {
  const [isWorking, setIsWorking] = useState(false);

  const handleConfirm = async () => {
    setIsWorking(true);
    try {
      await onConfirm();
      onClose();
    } catch (error) {
      console.error("Failed to finalize session:", error);
    } finally {
      setIsWorking(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Finalize Event Session</DialogTitle>
          <DialogDescription className="text-gray-600 dark:text-gray-400">
            <strong>Warning:</strong> Once the session is finalized, changes
            cannot be reverted. Are you sure you want to proceed?
          </DialogDescription>
        </DialogHeader>

        {waitlistCount > 0 && (
          <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">
                  Waitlist Notice
                </p>
                <p className="text-yellow-700 dark:text-yellow-300 mt-1">
                  There {waitlistCount === 1 ? "is" : "are"} currently{" "}
                  {waitlistCount} {waitlistCount === 1 ? "person" : "people"} on
                  the waitlist for this session. Finalizing will prevent any
                  further waitlist processing.
                </p>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button disabled={isWorking} onClick={onClose} variant="outline">
            Cancel
          </Button>
          <Button disabled={isWorking} onClick={handleConfirm}>
            {isWorking ? "Finalizing..." : "Finalize"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default FinalizeSessionModal;
